// Website templates: the page keys and the shared view-model (VM) vocabulary.
//
// ARCHITECTURE §4.1 / §4.3. Site core owns one controller hook per page key;
// each template supplies a whole-page view taking `({ vm })`. This file is
// the frozen list of page keys and the map from key to VM type. Each page's
// VM lives in `src/site/core/pages/<folder>/types.ts`.
//
// Adding a key here makes every template fail `tsc` until it implements the
// new page (SiteTemplate.pages is a mapped type over SitePageKey).

import type { AddonKey, SiteSectionKey } from '@/contexts/tenantAccess'
import type { HomePageVM } from '../pages/home/types'
import type { SchedulePageVM } from '../pages/schedule/types'
import type { StatsPageVM } from '../pages/stats/types'
import type { GamePageVM } from '../pages/game/types'
import type { PlayersPageVM } from '../pages/players/types'
import type { PlayerPageVM } from '../pages/player/types'
import type { TeamsPageVM } from '../pages/teams/types'
import type { TeamPageVM } from '../pages/team/types'
import type { BlogPageVM } from '../pages/blog/types'
import type { ContentPageVM } from '../pages/page/types'
import type { GalleryPageVM } from '../pages/gallery/types'
import type { SponsorPageVM } from '../pages/sponsor/types'
import type { VenuesPageVM } from '../pages/venues/types'
import type { VenuePageVM } from '../pages/venue/types'
import type { LiveEventsPageVM } from '../pages/live-events/types'
import type { LiveEventPageVM } from '../pages/live-event/types'
import type { TicketsPageVM } from '../pages/tickets/types'
import type { SalePageVM } from '../pages/sale/types'
import type { SignInPageVM } from '../pages/sign-in/types'
import type { RegistrationsPageVM } from '../pages/registrations/types'
import type { RegistrationPageVM } from '../pages/registration/types'
import type { AccountPageVM } from '../pages/account/types'
import type { ForgotPasswordPageVM } from '../pages/forgot-password/types'
import type { ResetPasswordPageVM } from '../pages/reset-password/types'
import type { JoinPageVM } from '../pages/join/types'
import type { MembersOnlyPageVM } from '../pages/members-only/types'

/**
 * The 26 templated page keys: the 18 of App.tsx's route order (1.0's 'form'
 * page was retired at registration step R), then the
 * pages added since, each mounted only with the strict registration add-on:
 * signIn (registration plan step 2), registrations and registration (the
 * public Register list and one registration's page, step 6), account,
 * forgotPassword and resetPassword (My account and password recovery,
 * step 9), and join (the invitation page, step 16); then membersOnly, the
 * private site's sign-in wall (step 33), which has no route of its own
 * (ROUTELESS_PAGES).
 */
export const PAGE_KEYS = [
  'home', 'schedule', 'stats', 'game', 'players', 'player', 'teams', 'team',
  'blog', 'page', 'gallery', 'sponsor', 'venues', 'venue',
  'liveEvents', 'liveEvent', 'tickets', 'sale', 'signIn',
  'registrations', 'registration', 'account', 'forgotPassword', 'resetPassword',
  'join', 'membersOnly',
] as const

export type SitePageKey = (typeof PAGE_KEYS)[number]

/** Page key → folder under src/site/core/pages/ (kebab-case for the camelCase keys). */
export const PAGE_FOLDERS: Record<SitePageKey, string> = {
  home: 'home', schedule: 'schedule', stats: 'stats', game: 'game',
  players: 'players', player: 'player', teams: 'teams', team: 'team',
  blog: 'blog', page: 'page', gallery: 'gallery', sponsor: 'sponsor',
  venues: 'venues', venue: 'venue', liveEvents: 'live-events', liveEvent: 'live-event',
  tickets: 'tickets', sale: 'sale', signIn: 'sign-in',
  registrations: 'registrations', registration: 'registration',
  account: 'account', forgotPassword: 'forgot-password', resetPassword: 'reset-password',
  join: 'join', membersOnly: 'members-only',
}

/** Page key → its view-model type. */
export interface PageVMMap {
  home: HomePageVM
  schedule: SchedulePageVM
  stats: StatsPageVM
  game: GamePageVM
  players: PlayersPageVM
  player: PlayerPageVM
  teams: TeamsPageVM
  team: TeamPageVM
  blog: BlogPageVM
  page: ContentPageVM
  gallery: GalleryPageVM
  sponsor: SponsorPageVM
  venues: VenuesPageVM
  venue: VenuePageVM
  liveEvents: LiveEventsPageVM
  liveEvent: LiveEventPageVM
  tickets: TicketsPageVM
  sale: SalePageVM
  signIn: SignInPageVM
  registrations: RegistrationsPageVM
  registration: RegistrationPageVM
  account: AccountPageVM
  forgotPassword: ForgotPasswordPageVM
  resetPassword: ResetPasswordPageVM
  join: JoinPageVM
  membersOnly: MembersOnlyPageVM
}

/**
 * Page keys with no route of their own. `membersOnly` (registration step 33):
 * SiteTemplateHost renders it in place of every gated route while a
 * members-only league's visitor is not admitted, through MembersOnlyRoute,
 * without the template Frame (the Frame's chrome reads are gated).
 */
export const ROUTELESS_PAGES: ReadonlySet<SitePageKey> = new Set<SitePageKey>(['membersOnly'])

// ── Shared VM vocabulary (§4.3) ─────────────────────────────────────────────

export type PageStatusKind = 'loading' | 'ready' | 'notFound' | 'error'

/**
 * Every page VM extends this. `loading` is the first load only (refetches
 * keep `ready` and expose `refreshing`); `notFound` is a typed 404, never
 * string-matched. `retry` MUST really refetch (#414).
 */
export interface PageStatus {
  status: PageStatusKind
  error: string | null
  retry: () => void
  refreshing?: boolean
  /**
   * `ready`, but the page's first wave of section data (stats, roster, games…)
   * is still in flight. Templates that lay out by that data keep their loading
   * skeleton until it lands, so nothing shifts on a cold load (#626). Refetches
   * never set it.
   */
  settling?: boolean
}

/** Gating facts. Templates respect them; they never call sectionEnabled/addonEntitled. */
export interface Gating {
  sections: Record<SiteSectionKey, boolean>
  addons: Record<AddonKey, boolean>
}

export interface Option<V> { value: V; label: string }

/** A data-affecting choice: the template renders it and calls `set`. */
export interface Select<V> { value: V; options: Option<V>[]; set: (v: V) => void }

/** A free-text input that affects data (search). `debounced` is what was fetched. */
export interface TextInput { value: string; debounced: string; set: (v: string) => void }

export interface Paged {
  total: number
  offset: number
  limit: number
  next?: () => void
  prev?: () => void
  loadMore?: () => void
  loadingMore?: boolean
}

/** Numbered pagination with an ellipsis model (blog, gallery). */
export interface Pagination {
  page: number
  pageCount: number
  /** e.g. [1, 'gap', 4, 5, 6, 'gap', 12] */
  items: Array<number | 'gap'>
  go: (page: number) => void
}

/** A per-sub-resource status (stats, game log, …). */
export type SubStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error'

export interface StatTile { key: string; label: string; value: string; sub?: string; emphasis?: boolean }
export interface StatColumn { key: string; label: string; short?: string; align?: 'left' | 'right' | 'center' }

/** A sanitised link the template can render as-is. */
export interface SafeLink { href: string; label: string; external: boolean }

// ── Fixtures (§4.7) ─────────────────────────────────────────────────────────

export type FixtureSport = 'basketball' | 'soccer'

/**
 * `src/site/core/pages/<folder>/fixtures.ts` default-exports one of these:
 * named VM builders (`full`, `empty`, `loading`, `notFound`, `error`, plus
 * page specifics such as `live`, `soldOut`). Actions are no-ops; tests wrap
 * them in spies. Fixtures are imported only by tests and the dev workbench.
 */
export type PageFixtures<K extends SitePageKey> = Record<string, (sport: FixtureSport) => PageVMMap[K]>

// ── Form fields (sale buyer form, public forms) ────────────────────────────

/**
 * One form field, validated by core. Templates MUST spread `inputProps()` on
 * the input: the 422 scroll-to-first-error finds fields by `name`.
 */
export interface FieldModel {
  name: string
  label: string
  type: 'text' | 'email' | 'tel' | 'number' | 'date' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'file'
  value: string
  set: (v: string) => void
  error: string | null
  required: boolean
  options?: Option<string>[]
  pattern?: string
  help?: string | null
  inputProps: () => Record<string, unknown>
}
